import {
  getMediaBlob,
  putMediaBlob,
  deleteMediaBlob,
  listCachedChecksums,
} from "./storage";
import { IPlayerMedia } from "./types";

/**
 * Offline media cache.
 *
 * Strategy: download every media file once, store the Blob in IndexedDB
 * keyed by `checksum`, and hand the player an `objectUrl` (blob://...)
 * to use as <img src> or <video src>. This works on every browser that
 * supports IndexedDB + URL.createObjectURL — including older LG webOS
 * and Samsung Tizen versions where Service Worker reliability is poor.
 *
 * If the same checksum appears across multiple playlists/devices we
 * dedupe automatically because checksum is the cache key.
 *
 * A Service Worker is *also* registered as a best-effort secondary
 * cache so direct media URL fetches (e.g. preloading) survive offline
 * on modern browsers. The player does NOT depend on the SW being
 * available — blob URLs are the source of truth.
 */

const objectUrlByChecksum = new Map<string, string>();

/**
 * Ensure the media blob is on-device. Returns an object URL the player
 * can use as src; or the original network URL if caching failed.
 */
export async function resolveMediaUrl(media: IPlayerMedia): Promise<string> {
  if (!media.checksum) {
    // No checksum to dedupe on — fall back to the network URL. Player
    // will work online; offline it will simply skip the item.
    return media.url;
  }

  const existingUrl = objectUrlByChecksum.get(media.checksum);
  if (existingUrl) return existingUrl;

  // Already in IDB?
  const cached = await getMediaBlob(media.checksum);
  if (cached) {
    const url = URL.createObjectURL(cached);
    objectUrlByChecksum.set(media.checksum, url);
    return url;
  }

  // Fetch + persist + return.
  try {
    const res = await fetch(media.url, { credentials: "omit" });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const blob = await res.blob();
    await putMediaBlob(media.checksum, blob);
    const url = URL.createObjectURL(blob);
    objectUrlByChecksum.set(media.checksum, url);
    return url;
  } catch {
    // Network failed and we have no cache — return original URL so
    // the player tries directly and surfaces an error per-element
    // (handled by the playback engine's onError).
    return media.url;
  }
}

/**
 * Prefetch every media item in the playlist tree in the background.
 * Runs serially to avoid saturating slow TV NICs. Errors are swallowed
 * per-item so a single broken URL doesn't stop the rest of the cache
 * from filling.
 */
export async function prefetchAll(items: IPlayerMedia[]): Promise<void> {
  for (const m of items) {
    try { await resolveMediaUrl(m); } catch { /* skip */ }
  }
}

/**
 * Drop EVERY cached media blob and revoke any object URLs we minted.
 * Used by the `clear_cache` remote command. The next call to
 * resolveMediaUrl() will re-download from the network.
 *
 * IndexedDB-level deletion is best-effort — if it fails we still
 * proceed so the player keeps working with whatever the server returns
 * next.
 */
export async function clearAll(): Promise<void> {
  // Revoke + drop every object URL we've created so the browser frees
  // the memory map; otherwise long-running tabs leak.
  objectUrlByChecksum.forEach((url) => {
    try { URL.revokeObjectURL(url); } catch { /* noop */ }
  });
  objectUrlByChecksum.clear();

  // Drop every IndexedDB blob.
  const keys = await listCachedChecksums();
  for (const k of keys) {
    await deleteMediaBlob(k);
  }
}

/**
 * Evict media blobs that are no longer referenced by the active
 * playlist. Called after a playlist refresh so old content doesn't
 * pin storage forever.
 */
export async function evictUnreferenced(activeChecksums: Set<string>): Promise<number> {
  const keys = await listCachedChecksums();
  let evicted = 0;
  for (const k of keys) {
    if (activeChecksums.has(k)) continue;
    await deleteMediaBlob(k);
    const url = objectUrlByChecksum.get(k);
    if (url) {
      try { URL.revokeObjectURL(url); } catch { /* noop */ }
      objectUrlByChecksum.delete(k);
    }
    evicted++;
  }
  return evicted;
}

/**
 * Best-effort SW registration. If the platform doesn't support SW (some
 * older TV browsers), this silently fails and the blob-URL strategy
 * keeps working.
 */
export function registerServiceWorker(): void {
  if (!("serviceWorker" in navigator)) return;
  // Defer past the first paint so it doesn't fight for bandwidth with
  // the initial /playlist + media downloads.
  setTimeout(() => {
    navigator.serviceWorker
      .register("/player-sw.js", { scope: "/player/" })
      .catch(() => { /* swallow — non-fatal */ });
  }, 5000);
}
